import { FilterOperators } from "@crispico/foundation-gwt-js";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { CrudHeader } from "@crispico/foundation-react/entity_crud/CrudHeader";
import { CrudViewerInEditor, sliceCrudViewerInEditor } from "@crispico/foundation-react/entity_crud/CrudViewerInEditor";
import { ConnectedComponentInSimpleComponent, ConnectedPageInfo, createSliceFoundation, getBaseImpures, getBaseReducers, PropsFrom, SliceFoundation, StateFrom } from "@crispico/foundation-react/reduxHelpers";
import { DEFAULT, ENTITY, ENT_ADD, ENT_AUDIT, ENT_SAVE, FIELDS_READ, GLOBAL, ENT_EXTERNAL_LINK, Utils} from "@crispico/foundation-react/utils/Utils";
import { MutationOptions, QueryOptions } from "apollo-client";
import { push } from "connected-react-router";
import { DocumentNode } from "graphql";
import gql from "graphql-tag";
import lodash, { initial } from "lodash";
import React, { ReactNode } from "react";
import { Link, RedirectProps, RouteProps } from "react-router-dom";
import { BreadcrumbSectionProps, Button, Container, Icon, Label, MenuItemProps, Message, Segment, SemanticShorthandCollection } from "semantic-ui-react";
import { apolloClientHolder, ApolloContext, apolloGetExtensionFromError, apolloGlobalErrorHandler, CatchedGraphQLError, GraphQLErrorExtensions } from "../apolloClient";
import { sliceColumnConfigDropdown } from "../components/ColumnConfig/ColumnConfigDropdown";
import { ColumnConfigDropdownSource } from "../components/ColumnConfig/dataStructures";
import { Filter } from "../components/CustomQuery/Filter";
import { OverrideableElement, TabRouterPane } from "../components/TabbedPage/TabbedPage";
import { Dashboard } from "../pages/dashboard/DashboardEntityDescriptor";
import { AbstractCrudPage, AbstractCrudPageProps, SliceAbstractCrudPage, sliceAbstractCrudPageOnlyForExtension } from "./AbstractCrudPage";
import { CrudFormInEditor, CrudFormInEditorProps } from "./CrudFormInEditor";
import { CrudGlobalSettings } from "./CrudGlobalSettings";
import { FieldDescriptorSettings } from "./CrudSettings";
import { EDITOR_PAGE_ICON, entityDescriptors, ID, enableAllFilters } from "./entityCrudConstants";
import { EntityDescriptor, FieldDescriptor } from "./EntityDescriptor";
import { sliceEntityTablePageOnlyForExtension } from "./EntityTablePage";
import { SaveFieldsParams } from "./SaveFieldsParams";
import { ShareLinkLogic } from "./ShareLinkLogic";

let AuditGraph: any;
let sliceAuditGraph: SliceFoundation;

init();

async function init() {
    const page = await import("../pages/Audit/AuditGraph");
    AuditGraph = page.AuditGraph;
    sliceAuditGraph = page.sliceAuditGraph;
}

export const ADD = "add";
export const DUPLICATE = "?duplicateFromId=";
export enum EditMode { ADD, EDIT_LOADING, EDIT, SAVING };

export type SaveParams = {
    performNavigationAtEnd?: boolean,
    initialFieldsAndValues?: any,
    componentProps?: EntityEditorPageProps
};

export class SliceEntityEditorPage extends SliceAbstractCrudPage {
    saveOperationName!: string;
    saveMutation!: DocumentNode;
    loadAttachedDashboardsOperationName: string = `dashboardService_attachedDashboards`;
    loadAttachedDashboardsQuery!: DocumentNode;
    // these are populated in AppMeta, to avoid circularity issues
    static sliceColumnConfigDropdown: any;
    static ColumnConfigDropdown: any;

    onBeforeMergeByConnectedPageHelper() {
        const ns = (this.nestedSlices as any);
        ns.columnConfigDropdown = SliceEntityEditorPage.sliceColumnConfigDropdown;
        // ns.dashboardTab = SliceEntityEditorPage.sliceDashboardTab;
        if (!this.entityDescriptor) {
            // I didn't have time to see if this check is OK
            // It happens on prot, somehow related to the CustomQueryEditor, embedded in the 
            // CQ dropdown, embedded in the CQ bar, embedded in a TablePage
            return;
        }

        if (this.canAddAuditTabs()) {
            const auditEntityDescriptor = entityDescriptors["Audit"];
            ns.auditTable = auditEntityDescriptor.infoTable.slice;
        }

        for (let td of this.entityDescriptor.tabDescriptors) {
            if (td.oneToManyEntityName) {
                const ed = entityDescriptors[td.oneToManyEntityName];
                if (!ed) { throw new Error("EntityDescriptor not found for: " + td.oneToManyEntityName); }
                if (!td.nestedSliceFieldName) { td.nestedSliceFieldName = ed.getLabel(true); }
                if (!td.nestedSlice) { td.nestedSlice = ed.infoTable.slice; }
            }
            ns[td.nestedSliceFieldName!] = td.nestedSlice;
        }
    }

    initIfNeeded() {
        if (this.loadQuery) { return; }

        this.initQueries();
    }

    protected getLoadOperationName(): string {
        return `${lodash.lowerFirst(this.entityDescriptor.name)}Service_findById`;
    }

    protected getSaveOperationName(): string {
        return `${lodash.lowerFirst(this.entityDescriptor.name)}Service_save`
    }

    protected getJavaIdType() {
        return this.entityDescriptor && this.entityDescriptor.javaIdType ? this.entityDescriptor.javaIdType : CrudGlobalSettings.INSTANCE.defaultJavaIdType;
    }

    protected getGraphQlIdType() {
        return this.entityDescriptor && this.entityDescriptor.graphQlIdType ? this.entityDescriptor.graphQlIdType : CrudGlobalSettings.INSTANCE.defaultGraphQlIdType;
    }

    protected initQueries() {
        this.loadOperationName = this.getLoadOperationName();
        this.loadQuery = gql(`query q($id: ${this.getGraphQlIdType()}!) { 
            ${this.loadOperationName}(id: $id) {
                ${this.getFieldsToRequest()}
            }
        }`);

        this.saveOperationName = this.getSaveOperationName();
        this.saveMutation = gql(`mutation q($params: SaveParams_${this.getJavaIdType()}Input) { 
            ${this.saveOperationName}(params: $params) { ${ID} }
        }`);

        this.loadAttachedDashboardsQuery = gql(`query q($id: Long, $forEditor: Boolean, $entityName: String) { 
            ${this.loadAttachedDashboardsOperationName}(id: $id, forEditor: $forEditor, entityName: $entityName) {
                id name configJson icon forEditor
            }
        }`);
    }

    /**
     * This method is meant to be overwritten by the editors that need to handle additional data received from the save operation.
     *
     * @author Daniela Buzatu
     */
    protected handleDataReturnedBySaveOperation(dataReturnedBySave: any): void {
    }

    /**
     * This method is meant to be overwritten by the editors that needs to enable save no matter the permissions of the logged in user.
     *
     * @author Daniela Buzatu
     */
    public isSaveAuthorized(editMode: any, showErrorMessageIfNoPermission?: boolean): boolean {
        const ed = this.entityDescriptor;
        const permission = Utils.pipeJoin([editMode === EditMode.ADD ? ENT_ADD : ENT_SAVE, ed.name]);
        return AppMetaTempGlobals.appMetaInstance.hasPermission(permission, showErrorMessageIfNoPermission);
    }

    /**
     * Temporary till the general validation error mechanism for an entity editor will be implemented.
     * Should be erased afterwards
     *
     * @author Daniela Buzatu
     */
    protected isDefaultErrorHandlerShownInCaseOfValidationException(): boolean {
        return false;
    }

    /**
     * The app fails to initialize if we try to add audit to CustomQuery. It is stuck in some loop where the page is blank and there is no console ouput.
     * I had no error suggesting this is caused by adding audit to CQ. I thought CQ could cause this and tried w/o audit and it worked.
     * Task for this issue: #29245 https://redmine.xops-online.com/issues/29245
     */
    public canAddAuditTabs() {
        return !["Audit", "CustomQuery"].includes(this.entityDescriptor.name);
    }

    nestedSlices = {
        crudViewerInEditor: sliceCrudViewerInEditor,
        // Because of the cycle: EntityEditorPage has a ColumnConfigDropdown which has a ColumnConfigEntityEditorPage which extends EntityEditorPage, there is a discussion:
        // this may or may not be here; actually it exists always except for ColumnConfigEntityEditorPage
        // having this dinamically here got us through a lot of problems:
        // 1) we had circularity issues on TS level
        // 2) circularity issues at runtime / imports
        // 3) infinite loop because of the merging/expanding algorithm in ConnectedPageHelper

        // columnConfigDropdown: sliceColumnConfigDropdown
    }

    initialState = {
        ...sliceAbstractCrudPageOnlyForExtension.initialState,
        // it's undefined during a few renders, until onMatchChanged() is called
        entity: undefined as any,
        mode: EditMode.EDIT_LOADING,
        // TODO CS: not used (yet); maybe we'll need it in the future?
        // fieldChanges: {} as { [fieldName: string]: any }

        /**
         * For subclasses, this needs to be updated. If the editor (subclass) has data that is not always
         * in the redux state, then `isDirty()` needs to be also overridden. 
         * 
         * @see isDirty()
         */
        dirty: false,
        duplication: false
    }

    reducers = {
        ...sliceAbstractCrudPageOnlyForExtension.reducers,
        ...getBaseReducers<SliceEntityEditorPage>(this),

        /**
         * Clears from the state: the entity. If extra data exists => should be overridden to clear it
         * as well.
         */
        clearStateBeforeAddOrEdit(state: StateFrom<SliceEntityEditorPage>) {
            state.dirty = false;
            state.mode = EditMode.EDIT_LOADING;
            state.entity = undefined;
        },

        /**
         * Called on mod ADD.
         * 
         * Instantiates a new entity and sets it in the state. May be overridden if extra data
         * needs to be generated (for a freshly created entity, which is probably = {}). This seems to be a scenario
         * with low probability.
         */
        onModeAdd(state: StateFrom<SliceEntityEditorPage>, oneToManyParams: string | null) {
            state.mode = EditMode.ADD;
            state.entity = this.getSlice().entityDescriptor.createNewEntity();
            if (oneToManyParams && oneToManyParams.includes(ADD + "?")) {
                oneToManyParams.split(ADD + "?")[1].split("&").forEach(f => {
                    const fieldAndValue = f.split("=");
                    const field = fieldAndValue[0];
                    const value = JSON.parse(fieldAndValue[1]);
                    state.entity[field] = value;
                });
            }
            state.entity.organization = global.currentOrganizationToFilterBy;
        },

        /**
         * Called on mode EDIT or ADD + duplicate (which is practically an EDIT). But for duplicate,
         * this method sets the mode to ADD.
         * 
         * Stores the entity in the state. Should be overridden if extra data exits. E.g. convert a JSON field in
         * a real object and store it in the state.
         */
        onModeLoaded(state: StateFrom<SliceEntityEditorPage>, entity: any) {
            state.mode = state.duplication ? EditMode.ADD : EditMode.EDIT;
            state.entity = entity;
        }
    }

    impures = {
        ...sliceAbstractCrudPageOnlyForExtension.impures,
        ...getBaseImpures<SliceEntityEditorPage>(this),

        async invokeLoadQuery(options: QueryOptions) {
            return await apolloClientHolder.apolloClient.query(options);
        },

        async load(id: any) {
            this.getDispatchers().clearStateBeforeAddOrEdit();
            if (typeof id === "string" && id.startsWith(ADD)) {
                this.getDispatchers().onModeAdd(id);
                return;
            } // else edit mode (or duplicate)

            const entity = (await this.invokeLoadQuery({ query: this.getSlice().loadQuery, variables: { id, pageSize: 20 } })).data[this.getSlice().loadOperationName];

            // it is needed to set the attached dashboards before the actual entity in order to
            // avoid mounting tabs twice (without then with the dashboards as part of the tabs)
            if (AppMetaTempGlobals.appMetaInstance.dashboardsAvailable && this.getSlice().entityDescriptor.hasAttachedDashboards) {
                const attachedDashboards = (await this.invokeLoadQuery({ query: this.getSlice().loadAttachedDashboardsQuery, variables: { id, forEditor: true, entityName: this.getSlice().entityDescriptor.name } })).data[this.getSlice().loadAttachedDashboardsOperationName];
                this.getDispatchers().setInReduxState({ attachedDashboards: attachedDashboards || []});
            }

            this.getDispatchers().onModeLoaded(entity);
            return entity;
        },

        async invokeSaveMutation(options: MutationOptions<any, { params: SaveFieldsParams }>) {
            return await apolloClientHolder.apolloClient.mutate(options);
        },

        /**
         * If there is extra data that needs to be embedded in the entity before save => this needs to be overridden.
         * Modify the entity (via copy, because the object comes from props, so is unmodifiable) and then call super. 
         * This entity will then be set in the state.
         */
        // TODO by CS: de unde e apelat cu acel navigate (de care deocamdata nu se mai tine cont); dar cu customFields?
        // I added params at the end; if needed move these 2 filds in it
        async save(entity: any, navigateToTable = true, customFieldsToUpdate?: string[], params?: SaveParams) {
            const initialMode = this.getState().mode;
            this.getDispatchers().setInReduxState({ mode: EditMode.SAVING })

            const ed = this.getSlice().entityDescriptor;

            if (!this.getSlice().isSaveAuthorized(initialMode, true)) {
                // TODO by CS: 1) mai bine faceam asta la inceputul metodei; 2) nu ar fi oricum ascuns butonul? Ala e nivelul de protectie client. Apoi va fi si cel de server;
                // cred ca e inutil verificarea de aici
                return;
            }

            const fields = customFieldsToUpdate ? customFieldsToUpdate : Object.keys(ed.fields).filter(f => ed.fields[f].enabled && !ed.fields[f].clientOnly);
            
            let fieldsAndValues: { [key: string]: any } = params?.initialFieldsAndValues ? params.initialFieldsAndValues : {};

            fields.forEach(f => {
                if (f !== "id") {
                    fieldsAndValues[f] = ed.fields[f] ? ed.fields[f].getFieldValue(entity) : entity[f];
                }
            });

            const saveFieldsParams: SaveFieldsParams = {
                id: this.getState().duplication ? null : entity.id,
                duplicateFromId: this.getState().duplication ? entity.id : null,
                fieldsAndValues
            };

            const { data } = (await this.invokeSaveMutation({
                context: {
                    [ApolloContext.ON_ERROR_HANDLER]: (e: CatchedGraphQLError) => {
                        this.getDispatchers().setInReduxState({ mode: initialMode });

                        if ("ValidationException" === apolloGetExtensionFromError(e, GraphQLErrorExtensions.EXCEPTION_SIMPLE_NAME)) {
                            // TODO by CS: aici vine codul care baga in state erorile
                            // TODO DB: Temporary till the mechanism for showing validation errors for entity editor will be implemented
                            return this.getSlice().isDefaultErrorHandlerShownInCaseOfValidationException();
                        } else {
                            apolloGlobalErrorHandler(e);
                            return true;
                        }
                    }
                },
                mutation: (this.getSlice() as SliceEntityEditorPage).saveMutation,
                variables: { params: saveFieldsParams },
            }));

            // if the corresponding table page is opened in the background, refresh the table after the entity is saved
            if (ed.infoTable.routeProps?.routeEntityName === AppMetaTempGlobals.appMetaInstance.helperAppContainer.dispatchers.getState().locationForMainRouteEntityName
                && AppMetaTempGlobals.appMetaInstance.newRootReducer.isHelperMounted(ed.infoTable.sliceName)) {
                    AppMetaTempGlobals.appMetaInstance.newRootReducer.getMountedHelper<typeof sliceEntityTablePageOnlyForExtension>(ed.infoTable.sliceName!).dispatchers.refresh();
            }

            const returnedId = data[(this.getSlice() as SliceEntityEditorPage).saveOperationName].id;

            this.getSlice().handleDataReturnedBySaveOperation(data);

            // we need to update the entity in the state, because maybe the subclass has modified it, w/ extra data
            this.getDispatchers().setInReduxState({ entity, mode: EditMode.EDIT, dirty: false });

            if (params?.componentProps?.modalProps || params?.performNavigationAtEnd === false) {
                return;
            }

            // when ADD / creating a new entity => redirects to newly created entity page,
            // when ADD / duplicating an existing entity => redirects to newly duplicated entity
            if (initialMode === EditMode.ADD) {
                this.getDispatchers().dispatch(push(ed.getEntityEditorUrl(returnedId)));
                // this.getDispatchers().setInReduxState({ mode: EditMode.EDIT, duplication: false });
            }
            return data;
        },

        async duplicate() {
            const ed = this.getSlice().entityDescriptor;
            this.getDispatchers().dispatch(push({
                pathname: ed.getEntityEditorUrl(ADD) + DUPLICATE + this.getState().entity.id
            }));
        }

    }

}

/**
 * As it's name suggests, this INSTANCE is provided for convenience for extension. In normal operation,
 * a new INSTANCE is created per entity.
 */
export const sliceEntityEditorPageOnlyForExtension = createSliceFoundation(class extends SliceEntityEditorPage { get entityDescriptor(): EntityDescriptor { throw new Error("This instance is only an utility for extension; it cannot be used.") } }, true);

export type EntityEditorPageProps = PropsFrom<SliceEntityEditorPage> & AbstractCrudPageProps & {
    columnConfigDropdown?: PropsFrom<typeof sliceColumnConfigDropdown>,
    // it seems that the 2 dispatchers are not merged; hence I comment and use "any" where needed
    // dispatchers: { columnConfigDropdown?: DispatchersFrom<typeof sliceColumnConfigDropdown> }

    pageHeaderClassName?: string,
    renderHeaderParams?: CrudEditorPageRenderHeaderParams

    onApply?: (entity: any) => void
    onSave?: (entity: any) => void

    scrollOnlyContentInEditor?: boolean
};

export type CrudEditorPageRenderHeaderParams = {
    columnConfig?: boolean
    hideDuplicate?: boolean
}

export class EntityEditorPage<P extends EntityEditorPageProps = EntityEditorPageProps> extends AbstractCrudPage<P> {

    editorFormSimpleClass = CrudFormInEditor;

    protected refFormSimple = React.createRef<CrudFormInEditor>();
    protected auditGraphConnectedPageInfo: ConnectedPageInfo | undefined = undefined;

    constructor(props: P) {
        super(props);

        this.onApply = this.onApply.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onDuplicate = this.onDuplicate.bind(this);
        this.onRevert = this.onRevert.bind(this);
        this.onBack = this.onBack.bind(this);
        this.onGotoTable = this.onGotoTable.bind(this);

        if (props.dispatchers.getSlice().canAddAuditTabs()) {
            this.auditGraphConnectedPageInfo = new ConnectedPageInfo(sliceAuditGraph, AuditGraph, "auditGraph_" + props.dispatchers.getSlice().entityDescriptor.name);
        }
    }

    protected onMatchChanged(match: any) {
        const duplication =  match.id.includes(DUPLICATE);
        this.props.dispatchers.setInReduxState({ duplication: duplication });
        if (duplication) {
            this.props.dispatchers.load(match.id.replace(ADD + DUPLICATE, ''));
        } else {
            this.props.dispatchers.load(match.id);
        }
    }

    protected getRedirectToFirstPaneRouteProps(redirectProps: RedirectProps): RouteProps {
        const props = super.getRedirectToFirstPaneRouteProps(redirectProps);
        props.exact = this.props.mode !== EditMode.ADD;
        return props;
    }

    protected getTitle(): string | { icon: JSX.Element | string; title: JSX.Element | string; } {
        const props = this.props;
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        return { icon: entityDescriptor.icon, title: (props.entity ? entityDescriptor.toMiniString(props.entity) : entityDescriptor.name) + " [" + _msg("entityCrud.editor.editor") + "]" };
    }

    /**
     * The form holds a temporary state (different from the Redux state). This is to avoid
     * updating the big state at each key stroke. The form transfer the data + dirty status
     * during `commitMain()` (i.e. on unmount/tab change or at the end, before save).
     * 
     * Hence, if we are asked about `isDirty()` before `commitMain()`, we need to extract
     * the info from the form itself, in addition to the `dirty` flag. Which is still needed
     * e.g. for the scenario: tab 1, modify, tab 2, tab 1. Now the form of tab 1 wouldn't be dirty.
     * 
     * If subclasses have similar components (such as forms), then this needs to be overridden.
     */
    protected isDirty() {
        if (this.props.mode === EditMode.ADD || this.props.mode === EditMode.EDIT) {
            return this.props.dirty || this.refFormSimple.current?.formikContext.dirty;
        }
        return false;
    }

    protected async onApply() {
        const { props } = this;
        await Utils.setTimeoutPromise(() => this.triggerCommitForAll());
        if (props.onApply) {
            await Utils.setTimeoutPromise(() => props.onApply!(this.props.entity)); // within setTimeout to wait for the state to propagate; also note that I use on purpose this.props
        }
        if (this.modalProps) {
            props.dispatchers.setModalOpen(false);
        }
    }

    protected async onSave() {
        await this.onApply();
        await this.onSaveInternal();
        if (this.props.onSave) {
            await Utils.setTimeoutPromise(() => this.props.onSave!(this.props.entity)); // within setTimeout to wait for the state to propagate; also note that I use on purpose this.props
        }
    }

    protected async onSaveInternal() {
        await this.props.dispatchers.save(this.props.entity, undefined, undefined, { componentProps: this.props });
    }

    /**
     * Only for modal mode.
     */
    protected onCancel() {
        this.props.dispatchers.setModalOpen(false);
    }

    protected async onDuplicate() {
        await this.onApply();
        this.props.dispatchers.duplicate();
    }

    protected async onAddDashboard() {
        // TODO LA: 4) this one wasn't implemented? + I wasn't able to modify an editor dashboard, the Edit button doesn't seem to work
    }

    protected async onRevert() {
        this.props.dispatchers.load(this.props.entity.id);
    }

    onBack() {
        AppMetaTempGlobals.history.goBack();
    }

    onGotoTable() {
        this.props.dispatchers.dispatch(push(this.props.dispatchers.getSlice().entityDescriptor.getEntityTableUrl()));
    }

    protected preRenderButtons(params: CrudEditorPageRenderHeaderParams): Array<OverrideableElement> {
        const props = this.props;
        const ed = this.props.dispatchers.getSlice().entityDescriptor;
        return [
            props.onApply && { elementType: Button, props: { key: "apply", "data-testid": "EntityEditorPage_apply", content: _msg("general.apply"), color: "green", onClick: this.onApply } },
            this.isSaveEnabled() && { elementType: Button, props: { key: "save", content: _msg("general.save"), primary: true, onClick: this.onSave, "data-testid": "EntityEditorPage_save" } },
            // TODO by CS: disabled for embedded mode, because not yet had a case, to properly test all cases, redirects, etc
            !params.hideDuplicate && !this.embeddedMode && (props.mode === EditMode.EDIT || props.mode === EditMode.EDIT_LOADING) && ed.hasDuplicateButton && { elementType: Button, props: { key: "duplicate", content: _msg('dto_crud.duplicate'), icon: "clone", disabled: this.props.mode !== EditMode.EDIT, onClick: this.onDuplicate } },
            // LA: option to add attached dashboards from editor page is not implemented at the moment, so I disabled the button
            // props.mode === EditMode.EDIT && ed.hasAttachedDashboards && { elementType: Button, props: { key: "addDashboard", content: 'Add dashboard', icon: "dashboard", disabled: this.props.mode !== EditMode.EDIT, onClick: this.onAddDashboard } },
            (props.mode === EditMode.EDIT || props.mode === EditMode.EDIT_LOADING) && { elementType: Button, props: { key: "revert", content: _msg("general.revert"), onClick: this.onRevert, disabled: this.props.mode !== EditMode.EDIT, "data-testid": "EntityEditorPage_revert" } },
            this.modalProps && { elementType: Button, props: { key: "cancel", content: _msg("general.cancel"), onClick: this.onCancel, "data-testid": "EntityEditorPage_cancel" } }
        ];
    }

    protected renderPageHeader() {
        const props = this.props;
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        let hasFieldsInHeader = false;
        let orderedFieldsInHeader: FieldDescriptorSettings[] = [];
        entityDescriptor.entityDescriptorSettings?.fieldDescriptorSettings.map(fds => {
            if (fds.inHeaderOrderIndex != undefined && fds.inHeaderOrderIndex != null) {
                orderedFieldsInHeader[fds.inHeaderOrderIndex] = fds;
                hasFieldsInHeader = true;
            }
        });
        let lineWithFields = !hasFieldsInHeader ? <></> : <>
            {orderedFieldsInHeader.map(fds => {
                const fd: FieldDescriptor = entityDescriptor.getField(fds.fieldRef);
                return <div key={fd.getFieldName()}>{fd.isCustomField || AppMetaTempGlobals.appMetaInstance.hasPermission(Utils.join([ENTITY, entityDescriptor.name, FIELDS_READ, fds.fieldRef])) ? fd.renderField(props.entity, { asLabel: true, showIcon: true, showTooltip: true, showMeasurementUnit: true }) : null}</div>;
            })}
        </>;
        return <CrudHeader className={(props.pageHeaderClassName || "") + (hasFieldsInHeader ? " CrudHeader_editorWithFieldsInHeader" : "CrudHeader_editorHeight")}
            backgroundImage={entityDescriptor.headerImage}
            content={this.renderPageHeaderContent(lineWithFields, undefined)}
        />;
    }

    protected getBreadcrumbSections(): SemanticShorthandCollection<BreadcrumbSectionProps> {
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        return [
            { key: 'Home', content: <><Icon name="home" /><Link to="/">{_msg("HomePage.title")}</Link></> },
            { key: "table", content: <>{entityDescriptor.getIcon()}<Link to={entityDescriptor.getEntityTableUrl()}>{entityDescriptor.getLabel(true)}</Link></> },
            { key: "editor", content: _msg("entityCrud.editor.subheader2") },
        ];
    }

    protected getHeaderIcon() {
        return <Icon size="big" name={EDITOR_PAGE_ICON} className="EntityCrudHeader_white" />;
    }

    protected renderPageHeaderContent(lineWithFields: ReactNode, additionalContent?: ReactNode): ReactNode {
        const { props } = this;
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        var rawExternalLink = global.crudSettings?.forEntities.find(fe => fe.entityName === entityDescriptor.name)?.externalLink;

        return <Segment className="EntityEditorPage_header_content">
            <div className="flex-container-row flex-center gap5">
                {this.getHeaderIcon()}
                <h2 className="EntityCrudHeader_white no-margin">{props.mode === EditMode.EDIT && props.entity ? entityDescriptor.toMiniString(props.entity) : entityDescriptor.getLabel()}</h2>
                {props.entity?.id && props.entity?.organization
                    ? entityDescriptor.getField("organization").renderField(props.entity, {
                        url: new ShareLinkLogic().createLink(false, entityDescriptor, enableAllFilters(Filter.create("organization.qualifiedName", FilterOperators.forString.like, props.entity.organization.qualifiedName + "%"))),
                        asLabel: true, showIcon: true, showTooltip: true
                    })
                    : null}
                {AppMetaTempGlobals.appMetaInstance.hasPermission(Utils.pipeJoin([ENT_EXTERNAL_LINK, entityDescriptor.name])) && !Utils.isNullOrEmpty(rawExternalLink)
                    ? <Label>
                        <Icon name="external alternate" link className="no-margin" onClick={() => {this.composeExternalLinkAndOpen(entityDescriptor, rawExternalLink!)}} />
                    </Label>
                    : null}
            </div>
            {additionalContent}
            <div className="flex-container-row flex-center gap5" style={{ marginTop: '5px' }}>
                {props.entity?.id ? lineWithFields : null}
            </div>
        </Segment >;
    }

    composeExternalLinkAndOpen (entityDescriptor: EntityDescriptor, rawExternalLink: string) {
            // match words between "${" and "}", will come like this "${testField}"
            const entityFieldsParams = rawExternalLink!.match(new RegExp('\[${](.*?)\}' , "g"))
            if (Utils.isNullOrEmpty(entityFieldsParams)){
                return;
            }

            entityFieldsParams!.forEach(param => {
                // param = ${testField} || ${anotherEntity.testField}
                // field = testField || anotherEntity.testField
                const field = param.replace("$", "").replace("{","").replace("}","");
                let fieldSplit = field.split(".");
                // "testField": fieldDescriptor for "testField"
                // "entity.testField": the first fieldDescriptor for "anotherEntity"
                let fieldDescriptor = entityDescriptor.getField(field);
                // get value for "testField" from entity 
                // get the anotherEntity {id: -1, testField: "value"}
                let valueOrEntity = fieldDescriptor.getFieldValue(this.props.entity);
                if (fieldSplit.length == 1) {
                    rawExternalLink = rawExternalLink!.replace(param, String(valueOrEntity));
                } else { 
                    // entityDescriptors["AnotherEntity"].getField("testField").getFieldValue({id: -1, testField: "value"})
                    rawExternalLink = rawExternalLink!.replace(param, 
                        String(entityDescriptors[fieldDescriptor.getType()].
                        getField(fieldSplit[fieldSplit.length - 1]).getFieldValue(valueOrEntity)));
                }
            })

            window.open(rawExternalLink)
    }

    renderHeader(params: CrudEditorPageRenderHeaderParams): ReactNode {
        const props = this.props;
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        // duplicated in CrudViewerInEditor :(
        // in theory, an editor may be non-modal. E.g. dashboard; in these case these buttons should appear?
        // const showBack = AppMetaTempGlobals.locationPathnamePrevious && AppMetaTempGlobals.locationPathnamePrevious !== this.props.dispatchers.getSlice().entityDescriptor.getEntityTableUrl();
        return (<>
            <Segment className="buttonBar EntityEditorFormSimple_bar">
                {/* {!this.modalProps && <>
                    {showBack && <Button icon="arrow left" content={_msg("dto_crud.back")} onClick={this.onBack} />}
                    <Button icon={!showBack && "arrow left"} content={showBack ? _msg("dto_crud.goToTable") : _msg("dto_crud.backToTable")} onClick={this.onGotoTable} />
                </>}
                <div className="EntityTablePage_barDivider" /> */}
                {params.columnConfig && props.columnConfigDropdown &&
                    <>
                        <SliceEntityEditorPage.ColumnConfigDropdown {...props.columnConfigDropdown} dispatchers={(props.dispatchers as any).columnConfigDropdown} source={ColumnConfigDropdownSource.EDITOR} entityDescriptor={entityDescriptor} />
                        <div className="EntityTablePage_barDivider" />
                    </>
                }
                {this.renderButtons(params)}
            </Segment>
            {this.props.duplication ? <Message>
                <Message.Header>{_msg('dto_crud.duplicate.message.header')}</Message.Header>
                <p>{_msg('dto_crud.duplicate.message.content', props.entity ? entityDescriptor.toMiniString(props.entity) : entityDescriptor.getLabel())}</p>
            </Message> : null}
        </>);
    }

    protected getMainRoutePath(): string {
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        return entityDescriptor.getEntityEditorUrl(this.props.match?.params.id);  // the ? for storybook
    }

    protected getMainPaneSubPath() {
        return "edit";
    }

    protected getMainMenuItemProps(): string | MenuItemProps {
        return { icon: "list alternate outline", content: _msg("entityCrud.editor.form"), "data-testid": "EntityEditorPage_edit" };
    }

    protected getPropsForFormSimple(): CrudFormInEditorProps {
        const { entityDescriptor } = this.props.dispatchers.getSlice();

        let columnsVisibleMap: { [field: string]: boolean } | undefined = undefined;
        if (this.props.columnConfigDropdown?.columnConfig) {
            columnsVisibleMap = {}
            // embedded in EntityEditorPage, so let's look at the column configs
            const columns = this.props.columnConfigDropdown.columnConfig.configObject.columns!;
            // convert the list into a map to use it easily below
            columnsVisibleMap = columns.reduce((map, current) => { map[current.name] = true; return map; }, {} as typeof columnsVisibleMap);
        }

        return {
            ref: this.refFormSimple, editor: this, hideButtonBar: true,
            // TODO by CS: asta nu e bine; am comentat caci nu e imposibil sa crape ceva; daca crapa, anuntati-ma va rog
            //  ...this.props, 
            entityDescriptor, entity: this.props.entity, columnsVisibleMap, columns: this.props.columnConfigDropdown?.columnConfig?.configObject.columns!
        }
    }

    /**
     * This is invoked when entity exists. For load => after load. For add => immediately.
     */
    protected renderForm() {
        return React.createElement(this.editorFormSimpleClass, this.getPropsForFormSimple());
    }

    protected isSaveEnabled(): boolean {
        return (this.props.dispatchers.getSlice() as SliceEntityEditorPage).isSaveAuthorized(this.props.mode);
    }

    protected getContainerCssClasses() {
        let noScroll = this.props.scrollOnlyContentInEditor;
        if (noScroll === undefined) {
            noScroll = AppMetaTempGlobals.appMetaInstance.scrollOnlyContentInEditor;
        }
        const outer = "EntityEditorPage_container " + (noScroll ? "flex-grow" : " ");
        const inner = "EntityEditorPage_segment " +  (noScroll ? "EntityEditorPage_containerOverflow" : "");
        return { outer: outer , inner: inner }
    }

    protected renderMain() {
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        if (this.props.mode === EditMode.ADD) {
            const permission = Utils.pipeJoin([ENT_ADD, entityDescriptor.name]);
            if (!AppMetaTempGlobals.appMetaInstance.hasPermission(permission)) {
                return AppMetaTempGlobals.appMetaInstance.getRedirectToError(permission, this.props.location);
            }
        }
        const cls = this.getContainerCssClasses();
        return <Container className={cls.outer} fluid>
            <Segment loading={this.props.mode === EditMode.SAVING} className={cls.inner}>
                {this.renderHeader(this.props.renderHeaderParams ? this.props.renderHeaderParams! : { columnConfig: true })}
                {this.props.entity ? this.renderForm() : <p>{_msg("general.loading")}</p>}
            </Segment>
        </Container>;
    };

    protected commitMain() {
        if (!this.refFormSimple.current) { return; }
        if (this.refFormSimple.current.formikContext.dirty) { this.props.dispatchers.setInReduxState({ dirty: true }) }
        this.props.dispatchers.setInReduxState({ entity: this.getEntityValuesFromForm() });
    }

    protected getEntityValuesFromForm() {
        return this.refFormSimple.current!.formikContext.values;
    }

    protected renderViewer() {
        const cls = this.getContainerCssClasses();
        return <Container className={cls.outer} fluid>
            <Segment className={cls.inner}>
                <CrudViewerInEditor {...this.props.crudViewerInEditor} dispatchers={this.props.dispatchers.crudViewerInEditor} editor={this} />
            </Segment>
        </Container>;
    }

    protected getExtraTabPanes(): (TabRouterPane | null)[] {
        const { entityDescriptor } = this.props.dispatchers.getSlice();
        let result: (TabRouterPane | null)[] = [...super.getExtraTabPanes()];
       
        if (AppMetaTempGlobals.appMetaInstance.showViewPropertiesTab) {
            result.push({
                routeProps: { path: `/viewer` },
                menuItemProps: { icon: "list alternate", content: _msg("entityCrud.editor.viewer") },
                render: () => this.renderViewer()
            });            
        }
        // if tabs were added above, set next the editor's place in list
        if (result.length > 0) {
            result.push(null);
        }

        if (AppMetaTempGlobals.appMetaInstance.hasPermission(Utils.pipeJoin([ENT_AUDIT, entityDescriptor.name]), false)) {
            const auditTableProps = (this.props as any).auditTable;
            if (auditTableProps) {
                const auditEntityDescriptor = entityDescriptors["Audit"];
                const menuItemProps: MenuItemProps = { icon: auditEntityDescriptor.icon, content: auditEntityDescriptor.getLabel(true) }; 
                const tabRouterPane: TabRouterPane = { menuItemProps: menuItemProps };
                tabRouterPane.routeProps = { path: "/" + auditEntityDescriptor.getLabel() };
                tabRouterPane.render = () => <auditEntityDescriptor.infoTable.wrappedComponentClass
                    {...auditTableProps} dispatchers={(this.props.dispatchers as any).auditTable} embeddedMode
                    oneToManyMode={{ field: "entityId", entity: this.props.entity, entityDescriptor, entityField: undefined }}
                />;
                result.push(tabRouterPane);
            }
            if (this.auditGraphConnectedPageInfo) {
                result.push({
                    routeProps: { path: "/auditGraph" },
                    menuItemProps: { icon: "chart line", content: _msg("AuditGraph.title") },
                    render: () => this.props.entity?.id ? <ConnectedComponentInSimpleComponent info={this.auditGraphConnectedPageInfo!}
                        entity={this.props.entity} entityDescriptor={this.props.dispatchers.getSlice().entityDescriptor} editor={this} /> : undefined
                })
            }
        }

        for (let td of entityDescriptor.tabDescriptors) {
            if (!td.tabRouterPane) { td.tabRouterPane = {} as any; }
            const tabRouterPane = td.tabRouterPane!;
            if (td.oneToManyEntityName) {
                const ed = entityDescriptors[td.oneToManyEntityName];
                if (!tabRouterPane.routeProps) { tabRouterPane.routeProps = { path: "/" + td.nestedSliceFieldName } }
                if (typeof tabRouterPane.menuItemProps !== "string") {
                    let mip = tabRouterPane.menuItemProps as MenuItemProps;
                    if (!mip) { mip = {}; tabRouterPane.menuItemProps = mip; }
                    if (!mip.icon) { mip.icon = ed.icon; }
                    if (!mip.content) { mip.content = ed.getLabel(true); }
                } // else string => don't do anything
                //@ts-ignore
                tabRouterPane.render = () => <ed.infoTable.wrappedComponentClass {...this.props[td.nestedSliceFieldName]} dispatchers={this.props.dispatchers[td.nestedSliceFieldName]}
                    embeddedMode oneToManyMode={{ field: td.oneToManyOppositeField, entity: this.props.entity, entityDescriptor, entityField: td.oneToManyEntityField }} />
            }
            if (td.render) {
                tabRouterPane.render = () => td.render!(this);
            } else if (td.component) {
                //@ts-ignore
                tabRouterPane.render = () => <td.component {...this.props[td.nestedSliceFieldName]} dispatchers={this.props.dispatchers[td.nestedSliceFieldName]}
                    entity={this.props.entity} />
            }
            result.push(tabRouterPane);
        }

        return result;
    }

    protected getExtraTabPanesInternal() {
        if (this.props.mode === EditMode.ADD) {
            return null;
        } else {
            return super.getExtraTabPanesInternal();
        }
    }

    protected renderAttachedDashboard(info: ConnectedPageInfo, dashboard: Dashboard) {
        return <ConnectedComponentInSimpleComponent info={info} dashboardEntity={dashboard} entityForAttachedDashboard={this.props.entity} currentOrganizationToFilterBy={this.props.currentOrganizationToFilterBy} />
    }

}
